import axios, { AxiosError } from 'axios';
import { TFunction } from 'i18next';
import { Dispatch, SetStateAction } from 'react';
import { ErrorOption, FieldValues, Path } from 'react-hook-form';

export type ErrorMessage =
  | 'invalid_grant'
  | 'Token has expired'
  | 'Account already exists'
  | 'There is no registered user with the given email address';
export type FormError = Record<string, string[]>;

export type AxiosBasicError = AxiosError<{
  message: unknown;
}>;

export type AxiosMessageError = AxiosError<{
  message: ErrorMessage;
}>;

export type AxiosValidationError = AxiosError<{
  message: FormError;
}>;

export const isAxiosError = (error: unknown): error is AxiosBasicError => {
  return axios.isAxiosError(error);
};

export const isAxiosMessageError = (
  error: unknown
): error is AxiosMessageError => {
  return (
    isAxiosError(error) && typeof error.response?.data?.message === 'string'
  );
};

export const isAxiosFormError = (
  error: unknown
): error is AxiosValidationError => {
  return (
    isAxiosError(error) && typeof error.response?.data?.message === 'object'
  );
};

export const getAxiosErrorMessageText = (
  error: AxiosMessageError,
  t: TFunction
) => {
  const code = error.response?.data.message;

  switch (code) {
    case 'invalid_grant': {
      return t('Invalid username or password.');
    }
    case 'Account already exists': {
      return t('An account with that email already exists');
    }
    case 'There is no registered user with the given email address': {
      return t('Found no user with the given email address');
    }
    case 'Token has expired': {
      return t('The token provided has expired');
    }
    default: {
      return t('Oops, something went wrong. Please try again later.');
    }
  }
};

/**
 * Pass setError and TFunction.
 *
 * This will read the data.response.message from the AxiosError, and then set it using the passed setError function.
 *
 * */
export const requestErrorHandler =
  (setError: (errorMessage: string) => void, t: TFunction) =>
  (error: unknown) => {
    if (!isAxiosMessageError(error)) {
      setError(t('Oops, something went wrong. Please try again later.'));
      return;
    }

    setError(getAxiosErrorMessageText(error, t));
  };

/**
 * Pass setError from react-hook-form, setGenericError and TFunction.
 *
 * This will read the data.response.message from the AxiosError, and then set errors using the passed setError function.
 *
 * setGenericError is used to set "common" errors that are not represented by a specific field. Such as "Account already exists" & "invalid_grant"
 *
 * */
export const requestFormErrorHandler =
  <T extends FieldValues>(
    setError: (name: Path<T>, error: ErrorOption) => void,
    setGenericError: Dispatch<SetStateAction<string>>,
    t: TFunction
  ) =>
  (error: unknown) => {
    if (!isAxiosError(error)) {
      setGenericError(t('Oops, something went wrong. Please try again later.'));
      return;
    }

    if (isAxiosMessageError(error)) {
      setGenericError(getAxiosErrorMessageText(error, t));
      return;
    }

    if (isAxiosFormError(error) && error.response?.data.message) {
      const errors = error.response?.data.message;
      Object.keys(errors).forEach(error => {
        setError(error as Path<T>, {
          message: errors[error].join(','),
        });
      });
    }
  };

export const handleReduxError = (
  error: unknown,
  t: TFunction
): Record<string, string[]> => {
  if (!isAxiosError(error)) {
    return {
      common: [t('Oops, something went wrong. Please try again later.')],
    };
  }

  if (isAxiosMessageError(error)) {
    return {
      common: [getAxiosErrorMessageText(error, t)],
    };
  }

  if (isAxiosFormError(error) && error.response?.data.message) {
    return error.response?.data.message;
  }

  return {};
};
